import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import Avatar from '../Avatar';
import { orgLabel } from '../../utils/orgTaxonomy';
import { useCall } from '../../context/CallContext';

/**
 * The call's right-hand dock, currently used for adding people.
 *
 * A panel inside the window rather than a modal over it, and that is the whole
 * point of the shape. The previous version reused the chat module's
 * PeoplePicker, which is `position: fixed` — inside a call window that is itself
 * fixed, clipped, and transform-animated on open, its footer was pushed off the
 * bottom edge and the Add button could not be reached at all. A panel that is an
 * ordinary flex child of the window cannot have that problem.
 *
 * It is also where the participant stack will live once a screen can be shared:
 * the shared screen takes the stage and the faces move in here. That is why it
 * is a dock beside the grid rather than a sheet on top of it — the layout has to
 * survive being used for two things.
 */

// Long enough that typing a name is one request rather than eight, short enough
// that the list feels like it is keeping up. Matches PeoplePicker.
const SEARCH_DEBOUNCE_MS = 250;

const CallSidePanel = ({ onClose }) => {
    const { call, inviteToCall, maxParticipants } = useCall();

    const [people, setPeople] = useState([]);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);

    const searchRef = useRef(null);

    useEffect(() => { searchRef.current?.focus(); }, []);

    useEffect(() => {
        let cancelled = false;

        // Debounced: a request per keystroke against a 500-person directory is
        // a lot of round trips for a list that barely changes.
        const timer = setTimeout(async () => {
            try {
                const res = await api.get('/conversations/contacts', { params: { q: query } });
                if (!cancelled) setPeople(res.data || []);
            } catch {
                if (!cancelled) setPeople([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, query ? SEARCH_DEBOUNCE_MS : 0);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [query]);

    const onCall = useMemo(
        // People, not seats: somebody on two devices is one person already here.
        () => new Set(call.participants.map((p) => String(p.personId || p.userId))),
        [call.participants]
    );

    const seatsLeft = Math.max(0, maxParticipants - call.participants.length);

    /*
     * Somebody who was selected and has since answered is no longer a person to
     * add — they are a person on the call. Dropping them here keeps the count in
     * the Add button honest, and stops the seat arithmetic double-counting them.
     */
    useEffect(() => {
        setSelected((prev) => {
            const kept = prev.filter((id) => !onCall.has(id));
            return kept.length === prev.length ? prev : kept;
        });
    }, [onCall]);

    const visible = people.filter((p) => !onCall.has(String(p._id)));
    const atLimit = selected.length >= seatsLeft;

    const toggle = (id) => {
        setSelected((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= seatsLeft) return prev;
            return [...prev, id];
        });
    };

    const add = () => {
        if (!selected.length) return;
        inviteToCall(selected);
        /*
         * Cleared, but the panel stays open. Adding people to a call is usually
         * more than one act — somebody does not answer, somebody else is
         * suggested — and closing it would make the second attempt start from
         * scratch. The tiles appearing in the grid are the confirmation.
         */
        setSelected([]);
    };

    return (
        <aside className="call-panel" aria-label="Add people to the call">
            <header className="call-panel-head">
                <h3>Add people</h3>
                <button
                    type="button"
                    className="call-panel-close"
                    onClick={onClose}
                    title="Close"
                    aria-label="Close panel"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </header>

            <div className="call-panel-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="call-panel-search-icon" />
                <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search people"
                    aria-label="Search people"
                />
            </div>

            <div className="call-panel-list">
                {loading && <p className="call-panel-empty">Loading…</p>}
                {!loading && !visible.length && (
                    <p className="call-panel-empty">
                        {query ? 'Nobody matches that.' : 'Everyone available is already here.'}
                    </p>
                )}

                {visible.map((person) => {
                    const id = String(person._id);
                    const picked = selected.includes(id);
                    return (
                        <button
                            type="button"
                            key={id}
                            className={[
                                'call-panel-person',
                                picked && 'is-picked',
                                !picked && atLimit && 'is-disabled'
                            ].filter(Boolean).join(' ')}
                            onClick={() => toggle(id)}
                            aria-pressed={picked}
                            disabled={!picked && atLimit}
                        >
                            <Avatar
                                name={person.name}
                                profilePic={person.profilePic}
                                className="call-panel-avatar"
                            />
                            <span className="call-panel-person-text">
                                <span className="call-panel-person-name">{person.name}</span>
                                <span className="call-panel-person-sub">
                                    {[orgLabel(person.jobTitle) || person.role, orgLabel(person.department)]
                                        .filter(Boolean).join(' · ')}
                                </span>
                            </span>
                            {picked && <FontAwesomeIcon icon={faCheck} className="call-panel-tick" />}
                        </button>
                    );
                })}
            </div>

            <footer className="call-panel-foot">
                <span className="call-panel-seats">
                    {seatsLeft === 0
                        ? 'This call is full'
                        : `${seatsLeft - selected.length} more can join`}
                </span>
                <button
                    type="button"
                    className="call-panel-add"
                    onClick={add}
                    disabled={!selected.length}
                >
                    {selected.length ? `Add ${selected.length}` : 'Add'}
                </button>
            </footer>
        </aside>
    );
};

export default CallSidePanel;
